import { bytesToHex } from "@noble/hashes/utils";
import { sha256 as sha256Stream } from "@noble/hashes/sha2";

import type { InstallFile, InstallFileSegment, LaneManifest } from "@/runtime/events";

import {
  openOpfsWritable,
  readOpfsFileSize,
  removeOpfsFile,
  walkOpfsRoot,
  writeBlobToOpfs,
} from "@/storage/opfs";

export type OpfsWriterMode = "sync-access-handle" | "writable-stream";

export interface InstallTelemetry {
  event: string;
  level?: "debug" | "info" | "warning" | "error";
  msg?: string;
  data?: Record<string, unknown>;
}

export interface InstallStepOptions {
  onTelemetry?: (telemetry: InstallTelemetry) => void;
}

export interface InstallFileResult {
  bytes: number;
  durationMs: number;
  mode: OpfsWriterMode | "blob-fallback";
  chunksSeen: number;
}

export interface InstallFileSegmentResult {
  segmentIndex: number;
  offset: number;
  bytes: number;
  durationMs: number;
  mode: OpfsWriterMode;
  chunksSeen: number;
}

export function getOpfsPathForManifestFile(manifest: LaneManifest, filePath: string): string {
  return getOpfsPathForFilePath(manifest.lane, manifest.id, filePath);
}

export function getOpfsPathForFilePath(
  lane: string,
  manifestId: string,
  filePath: string,
): string {
  const fileName = filePath.split("/").pop();

  if (!fileName) {
    throw new Error(`Invalid manifest path: ${filePath}`);
  }

  return `${lane}/${manifestId}/${fileName}`;
}

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function sha256Blob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return toHex(digest);
}

interface OpfsChunkWriter {
  mode: OpfsWriterMode;
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

interface OpenWriterOptions {
  truncate?: boolean;
  startOffset?: number;
}

async function openOpfsChunkWriter(
  path: string,
  options: OpenWriterOptions = {},
): Promise<OpfsChunkWriter> {
  // Default path: FileSystemWritableFileStream.
  //
  // iOS Safari's FileSystemSyncAccessHandle write path inflates the UA's
  // `storage.estimate().usage` report in power-of-2 extent sizes — we wrote
  // 60 MB of bytes and `usage` reported 452 MB, an 8–16× inflation that
  // persists across a 250 ms settle window. This directly contributes to
  // iOS tab-eviction pressure (see `docs/07` and the 2026-04-24 telemetry
  // trace in `data/dev-harness/browser-telemetry.jsonl` for
  // page_id=pg-54ebee3f-a2).
  //
  // FileSystemWritableFileStream writes to a temporary file that is
  // atomically renamed at `close()`. On Chromium the per-segment delta
  // matches bytes-written 1:1. Whether iOS behaves the same is the next
  // measurement; telemetry stays on and will tell us.
  //
  // `keepExistingData: true` preserves content outside the write range so
  // segment N of a segmented file does not erase segments 0..N-1. This is
  // spec-required for this codepath to work at all. The spec allows the
  // implementation to copy-on-write or lazy-clone; in practice Safari 16+
  // handles this but performance under segmented install at scale is part
  // of what we're measuring.
  //
  // `openOpfsSyncAccessHandle` remains exported from `@/storage/opfs` for
  // callers that need synchronous low-level access. We just no longer use
  // it on the install write path.
  const writable = await openOpfsWritable(path, {
    keepExistingData: !options.truncate,
  });

  if (options.startOffset && options.startOffset > 0) {
    await writable.seek(options.startOffset);
  }

  let closed = false;
  return {
    mode: "writable-stream",
    async write(chunk) {
      await writable.write(chunk as unknown as BufferSource);
    },
    async close() {
      if (closed) {
        return;
      }
      closed = true;
      await writable.close();
    },
    async abort() {
      if (closed) {
        return;
      }
      closed = true;
      try {
        await writable.abort();
      } catch {
        // best-effort cleanup; the OPFS file is removed by the caller.
      }
    },
  };
}

/**
 * Install a single non-segmented file: one fetch, one streaming hash, one OPFS write,
 * verify full-file SHA-256, close handle. Used for files whose total size is under the
 * 4 MiB segment threshold.
 */
export async function installFile(
  lane: string,
  manifestId: string,
  file: InstallFile,
  options: InstallStepOptions = {},
): Promise<InstallFileResult> {
  const opfsPath = getOpfsPathForFilePath(lane, manifestId, file.path);
  const startedAt = performance.now();

  const response = await fetch(file.path, { cache: "no-store" });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${file.path}: status ${response.status}`);
  }

  if (!response.body) {
    const blob = await response.blob();
    const actualHash = await sha256Blob(blob);
    if (actualHash !== file.sha256) {
      throw new Error(`Checksum mismatch for ${file.path}`);
    }
    await writeBlobToOpfs(opfsPath, blob);
    options.onTelemetry?.({
      event: "install.file_done",
      data: {
        lane,
        manifest_id: manifestId,
        file: file.path,
        mode: "blob-fallback",
        bytes: blob.size,
        chunks_seen: 1,
        elapsed_ms: Math.round(performance.now() - startedAt),
      },
    });
    return {
      bytes: blob.size,
      durationMs: Math.round(performance.now() - startedAt),
      mode: "blob-fallback",
      chunksSeen: 1,
    };
  }

  const writer = await openOpfsChunkWriter(opfsPath, { truncate: true, startOffset: 0 });

  options.onTelemetry?.({
    event: "install.writer_mode",
    data: {
      lane,
      manifest_id: manifestId,
      file: file.path,
      mode: writer.mode,
      role: "whole-file",
    },
  });

  const hash = sha256Stream.create();
  const reader = response.body.getReader();
  let received = 0;
  let chunkCount = 0;
  // Same iOS Safari mitigation as the segmented path: accumulate chunks into
  // a single buffer and do one `writer.write()` at end. iPhone session
  // pg-41bc8016 traced ~549 MB of phantom `storage.estimate().usage` growth
  // across 6 whole-file installs during STT (tokenizer.json alone is 3.7 MB
  // in many small fetch chunks → many `write()` calls inside one
  // `createWritable()` session → per-write extent-doubling on iOS). Whole-file
  // installs are by manifest design under the 4 MiB segment threshold, so the
  // memory footprint of the accumulator is bounded.
  const collectedChunks: Uint8Array[] = [];

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      if (!value?.byteLength) {
        continue;
      }
      hash.update(value);
      collectedChunks.push(value);
      received += value.byteLength;
      chunkCount += 1;
    }

    const fullBuffer = new Uint8Array(received);
    let offset = 0;
    for (const chunk of collectedChunks) {
      fullBuffer.set(chunk, offset);
      offset += chunk.byteLength;
    }
    await writer.write(fullBuffer);
    await writer.close();
  } catch (error) {
    await writer.abort();
    await removeOpfsFile(opfsPath).catch(() => undefined);
    throw error;
  }

  const actualHash = bytesToHex(hash.digest());
  if (actualHash !== file.sha256) {
    await removeOpfsFile(opfsPath).catch(() => undefined);
    throw new Error(`Checksum mismatch for ${file.path}`);
  }

  const result: InstallFileResult = {
    bytes: received,
    durationMs: Math.round(performance.now() - startedAt),
    mode: writer.mode,
    chunksSeen: chunkCount,
  };

  options.onTelemetry?.({
    event: "install.file_done",
    data: {
      lane,
      manifest_id: manifestId,
      file: file.path,
      mode: writer.mode,
      bytes: received,
      chunks_seen: chunkCount,
      elapsed_ms: result.durationMs,
    },
  });

  return result;
}

/**
 * Install one segment of a segmented file. Uses an HTTP Range request that MUST
 * return 206 Partial Content; falls back to 200 only when offset === 0 and the
 * segment covers the entire file (defensive). Opens a fresh sync access handle,
 * writes at the segment's absolute offset, verifies the per-segment SHA-256,
 * flushes and closes. Throws on any mismatch — the caller decides whether to
 * delete the partial OPFS file and restart from segment 0.
 */
async function safeStorageEstimate(): Promise<StorageEstimate | null> {
  if (
    typeof navigator === "undefined" ||
    !navigator.storage ||
    typeof navigator.storage.estimate !== "function"
  ) {
    return null;
  }
  try {
    return await navigator.storage.estimate();
  } catch {
    return null;
  }
}

const SETTLE_POLL_DELAY_MS = 250;

// Module-level counter: how many bytes we've told the installer to write in
// this worker session. Useful to diff against what the UA reports as origin
// usage and against the OPFS walk's ground-truth total-on-disk. This counter
// does NOT persist across worker spawns — since the orchestrator now keeps a
// single worker for the life of an install, that's fine.
let sessionBytesWritten = 0;

function safeDelay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    try {
      globalThis.setTimeout(resolve, ms);
    } catch {
      resolve();
    }
  });
}

interface SegmentStageFailureInput {
  lane: string;
  manifestId: string;
  filePath: string;
  segmentIndex: number;
  attemptNumber?: number;
  offset: number;
  bytes: number;
  stage: "fetch" | "open-writer" | "stream-read" | "write" | "close" | "verify";
  err: unknown;
  received?: number;
  chunkCount?: number;
  writeTotalMs?: number;
  ttfbMs?: number | null;
  usageBeforeBytes?: number | null;
  usageQuotaBytes?: number | null;
  usageAtFailureBytes?: number | null;
}

// Emit a rich failure event with the iOS-specific error metadata that the
// generic worker.task_failure path discards. iOS Safari's OPFS layer
// throws DOMExceptions whose `.name` and `.code` distinguish "transient
// memory pressure, retry me" from "real quota exhausted" from "writer
// state invalid". We need all of: stage, error.name, error.code,
// message, stack snippet, plus storage state right before vs at failure.
function emitSegmentStageFailure(
  options: { onTelemetry?: (t: { event: string; level?: "debug" | "info" | "warning" | "error"; msg?: string; data?: Record<string, unknown> }) => void },
  input: SegmentStageFailureInput,
): void {
  const errAny = input.err as { name?: string; code?: number; message?: string; stack?: string } | undefined;
  const errName = typeof errAny?.name === "string" ? errAny.name : null;
  const errCode = typeof errAny?.code === "number" ? errAny.code : null;
  const errMessage = typeof errAny?.message === "string" ? errAny.message : String(input.err);
  const errStack = typeof errAny?.stack === "string" ? errAny.stack.split("\n").slice(0, 6).join("\n") : null;
  options.onTelemetry?.({
    event: "install.segment_stage_fail",
    level: "warning",
    msg: errMessage,
    data: {
      lane: input.lane,
      manifest_id: input.manifestId,
      file: input.filePath,
      segment_index: input.segmentIndex,
      attempt_number: input.attemptNumber ?? 1,
      offset: input.offset,
      bytes: input.bytes,
      failed_stage: input.stage,
      // Error fingerprinting — most important for "transient" vs "real" classification.
      error_name: errName,
      error_code: errCode,
      error_constructor: errAny?.constructor?.name ?? null,
      error_message: errMessage,
      error_stack_head: errStack,
      // Progress at the moment of failure.
      received_bytes: input.received ?? null,
      chunks_seen: input.chunkCount ?? null,
      write_total_ms: input.writeTotalMs ?? null,
      ttfb_ms: input.ttfbMs ?? null,
      // Storage state. Three points if available: before this segment
      // started, at the moment we tried (and failed), and the quota.
      opfs_usage_before_bytes: input.usageBeforeBytes ?? null,
      opfs_usage_at_failure_bytes: input.usageAtFailureBytes ?? null,
      opfs_usage_at_failure_minus_before:
        input.usageAtFailureBytes !== null && input.usageAtFailureBytes !== undefined &&
        input.usageBeforeBytes !== null && input.usageBeforeBytes !== undefined
          ? input.usageAtFailureBytes - input.usageBeforeBytes
          : null,
      opfs_quota_bytes: input.usageQuotaBytes ?? null,
    },
  });
}

// iOS Safari throws DOMException("UnknownError") from the writable-file-stream
// layer when the WebContent process is briefly under memory pressure. WebKit
// docs (MDN DOMException, WebKit storage policy) describe UnknownError as a
// generic transient/OOM-style condition; true quota exhaustion throws
// QuotaExceededError. Confirmed live on iPhone 18.x mid-Qwen install.
//
// We KEEP this classifier (used by the orchestrator and bench to label and
// react to the failure) but no longer retry inside the same worker — see
// commentary in installFileSegment. Per consultant 2026-04-27: in-worker
// retry leaves phantom OPFS bytes per attempt and can put the worker into a
// non-responsive state. The right reaction is fail-fast + worker termination.
function isIosTransientOpfsError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | undefined;
  if (!e) return false;
  const message = typeof e.message === "string" ? e.message : "";
  if (e.name === "UnknownError") return true;
  if (e.name === "InvalidStateError" && /transient|out of memory/i.test(message)) return true;
  if (/transient reason \(e\.g\. out of memory\)/i.test(message)) return true;
  return false;
}

export async function installFileSegment(
  lane: string,
  manifestId: string,
  filePath: string,
  segment: InstallFileSegment,
  segmentIndex: number,
  options: InstallStepOptions & {
    resetFile?: boolean;
    /** True when this is the first segment of a file — do the expensive
     *  ground-truth OPFS walk for a pre-file baseline. */
    isFirstSegment?: boolean;
    /** True when this is the last segment of a file — do the expensive
     *  ground-truth OPFS walk for a post-file verification. */
    isLastSegment?: boolean;
  } = {},
): Promise<InstallFileSegmentResult> {
  // Single attempt. The retry-with-backoff wrapper that lived here was
  // rolled back per consultant guidance 2026-04-27: each retry left
  // ~4 MiB of phantom OPFS usage and after 3 retries the heavy worker
  // froze (no setInterval/setTimeout callbacks fired in that worker for
  // ~10 s), leaving the install lane hung indefinitely with no
  // .lane.fail event. The right response to an iOS UnknownError on the
  // OPFS write is fail-fast: surface the failure to the orchestrator,
  // let the caller decide whether to terminate the worker and resume
  // from per-segment checkpoints in a fresh worker context. The
  // worker-deadline wrapper in heavy-operation-client (added separately)
  // ensures a stuck worker can no longer wedge the UI forever.
  //
  // The iOS-transient classifier (isIosTransientOpfsError) is kept in
  // the file so the orchestrator can use it to label failures, decide
  // whether to terminate the worker before retrying as a fresh task,
  // and decide checkpoint-vs-restart-from-scratch policy.
  return installFileSegmentOnce(lane, manifestId, filePath, segment, segmentIndex, options, 1);
}

export { isIosTransientOpfsError };

async function installFileSegmentOnce(
  lane: string,
  manifestId: string,
  filePath: string,
  segment: InstallFileSegment,
  segmentIndex: number,
  options: InstallStepOptions & {
    resetFile?: boolean;
    isFirstSegment?: boolean;
    isLastSegment?: boolean;
  } = {},
  attemptNumber = 1,
): Promise<InstallFileSegmentResult> {
  const opfsPath = getOpfsPathForFilePath(lane, manifestId, filePath);
  const startedAt = performance.now();

  // Pressure telemetry: cheap per-segment `storage.estimate()` + (only at
  // file boundaries) expensive ground-truth OPFS walk. The walk iterates every
  // file in the origin; on large origins that costs hundreds of ms per call,
  // so running it per segment blows the install time up by minutes. Running
  // it at file start + file end keeps total overhead bounded to ~2 walks per
  // file regardless of segment count.
  const estimateBefore = await safeStorageEstimate();
  const walkBefore = options.isFirstSegment ? await walkOpfsRoot() : null;
  const sessionBytesBefore = sessionBytesWritten;

  options.onTelemetry?.({
    event: "install.segment_pressure_before",
    data: {
      lane,
      manifest_id: manifestId,
      file: filePath,
      segment_index: segmentIndex,
      attempt_number: attemptNumber,
      offset: segment.offset,
      bytes: segment.bytes,
      is_first_segment: Boolean(options.isFirstSegment),
      opfs_usage_bytes: estimateBefore?.usage ?? null,
      opfs_quota_bytes: estimateBefore?.quota ?? null,
      opfs_walk_total_bytes: walkBefore?.totalBytes ?? null,
      opfs_walk_file_count: walkBefore?.fileCount ?? null,
      opfs_walk_dir_count: walkBefore?.dirCount ?? null,
      opfs_walk_duration_ms: walkBefore?.durationMs ?? null,
      opfs_walk_top: walkBefore?.top ?? null,
      opfs_walk_error: walkBefore?.error ?? null,
      session_bytes_written: sessionBytesBefore,
    },
  });

  const rangeHeader = `bytes=${segment.offset}-${segment.offset + segment.bytes - 1}`;

  const fetchStartedAt = performance.now();
  let response;
  try {
    response = await fetch(filePath, {
      cache: "no-store",
      headers: { Range: rangeHeader },
    });
  } catch (err) {
    emitSegmentStageFailure(options, {
      lane, manifestId, filePath, segmentIndex, attemptNumber,
      offset: segment.offset, bytes: segment.bytes,
      stage: "fetch", err,
      usageBeforeBytes: estimateBefore?.usage ?? null,
      usageQuotaBytes: estimateBefore?.quota ?? null,
    });
    throw err;
  }
  const fetchHeadersAt = performance.now();

  if (response.status === 200) {
    throw new Error(
      `Range fetch returned 200 OK for segmented file ${filePath} (expected 206); ` +
        `aborting to avoid silently downloading the whole file in iPhone tab mode`,
    );
  }

  if (response.status !== 206) {
    throw new Error(
      `Range fetch for ${filePath} returned ${response.status} (expected 206 Partial Content)`,
    );
  }

  if (!response.body) {
    throw new Error(`Range fetch for ${filePath} returned no body`);
  }

  // Stage-by-stage telemetry: when a segment fails, we want to know which
  // stage threw. Track the current stage in a closed-over var so the
  // catch-block error report can include it. iPhone iOS has been observed
  // to throw "The operation failed for an unknown transient reason
  // (e.g. out of memory)" — that wording is iOS Safari's OPFS layer
  // asking us to back off, but until we capture stage + error name + code
  // we cannot tell whether the throw came from openOpfsChunkWriter,
  // writer.write, writer.close, fetch, reader.read, or hash verify.
  let stage: "fetch" | "open-writer" | "stream-read" | "write" | "close" | "verify" = "open-writer";

  const writer = await (async () => {
    try {
      return await openOpfsChunkWriter(opfsPath, {
        truncate: Boolean(options.resetFile),
        startOffset: segment.offset,
      });
    } catch (err) {
      emitSegmentStageFailure(options, {
        lane, manifestId, filePath, segmentIndex, attemptNumber,
        offset: segment.offset, bytes: segment.bytes,
        stage: "open-writer", err,
        usageBeforeBytes: estimateBefore?.usage ?? null,
        usageQuotaBytes: estimateBefore?.quota ?? null,
      });
      throw err;
    }
  })();

  options.onTelemetry?.({
    event: "install.writer_mode",
    data: {
      lane,
      manifest_id: manifestId,
      file: filePath,
      mode: writer.mode,
      role: "segment",
      segment_index: segmentIndex,
      offset: segment.offset,
      bytes: segment.bytes,
      reset_file: Boolean(options.resetFile),
      // OPFS accounting at segment START. `usage` from storage.estimate() is
      // the origin's total byte footprint as the UA reports it. Watching this
      // grow segment-over-segment tells us whether OPFS accounting is what's
      // charging tab memory on iOS.
      opfs_usage_before_bytes: estimateBefore?.usage ?? null,
      opfs_quota_bytes: estimateBefore?.quota ?? null,
    },
  });

  const hash = sha256Stream.create();
  const reader = response.body.getReader();
  let received = 0;
  let chunkCount = 0;
  let peakChunkBytes = 0;
  let ttfbMs: number | null = null;
  let maxReadGapMs = 0;
  let lastReadAt = fetchHeadersAt;
  let writeTotalMs = 0;

  // bench/03-fetch-vs-buffer-inflation isolated the iOS Safari install-time
  // inflation source: streaming `response.body.getReader()` and writing each
  // chunk to OPFS as it arrives charges `storage.estimate().usage` per
  // `write()` call (5–13× the actual bytes on iPhone). The same fetch + same
  // write API but with one batched `write()` per segment shows 1.00×.
  // So: keep the streaming reader (so we keep TTFB / read-gap / hash / progress
  // telemetry), but accumulate chunks into a single `Uint8Array` for the
  // segment and do exactly one `writer.write(buffer)` at end-of-segment.
  // Memory cost is bounded by segment size (~4 MiB), which we already pay
  // implicitly via the fetch reader's queue.
  const segmentBuffer = new Uint8Array(segment.bytes);

  try {
    stage = "stream-read";
    while (true) {
      const readStartedAt = performance.now();
      const readGapMs = readStartedAt - lastReadAt;
      const { done, value } = await reader.read();
      const readReturnedAt = performance.now();
      lastReadAt = readReturnedAt;
      if (ttfbMs === null && (value?.byteLength ?? 0) > 0) {
        ttfbMs = Math.round(readReturnedAt - fetchStartedAt);
      }
      if (readGapMs > maxReadGapMs) {
        maxReadGapMs = readGapMs;
      }
      if (done) {
        break;
      }
      if (!value?.byteLength) {
        continue;
      }
      if (value.byteLength > peakChunkBytes) {
        peakChunkBytes = value.byteLength;
      }
      // Reject overrun before we corrupt buffer/hash state. A misbehaving
      // origin or proxy could in principle ship more bytes than the Range
      // window asked for; surface that as a clean failure.
      if (received + value.byteLength > segment.bytes) {
        throw new Error(
          `Segment ${segmentIndex} of ${filePath} overran: got ${received + value.byteLength} bytes, expected ${segment.bytes}`,
        );
      }
      hash.update(value);
      segmentBuffer.set(value, received);
      received += value.byteLength;
      chunkCount += 1;
    }

    // One write per segment. This is the inflation fix.
    stage = "write";
    const writeStart = performance.now();
    const writeView = received === segment.bytes ? segmentBuffer : segmentBuffer.subarray(0, received);
    await writer.write(writeView);
    writeTotalMs = performance.now() - writeStart;
    stage = "close";
    await writer.close();
  } catch (error) {
    // Capture iOS-specific OPFS error wording before we abort/throw. iOS
    // returns "The operation failed for an unknown transient reason
    // (e.g. out of memory)" with name="InvalidStateError" or
    // "QuotaExceededError" — those names + .code disambiguate "iOS asked
    // us to retry" from "actual quota wall". Captured at the FAILED stage
    // so the receiver can see fetch vs stream-read vs write vs close.
    emitSegmentStageFailure(options, {
      lane, manifestId, filePath, segmentIndex, attemptNumber,
      offset: segment.offset, bytes: segment.bytes,
      stage, err: error,
      received,
      chunkCount,
      writeTotalMs: Math.round(writeTotalMs),
      ttfbMs,
      usageBeforeBytes: estimateBefore?.usage ?? null,
      usageQuotaBytes: estimateBefore?.quota ?? null,
      // Take a fresh estimate at failure moment so we can compare to
      // before+other segment endings. iOS Safari may report quota
      // pressure asymmetrically: ok at segment_done time, but ramp up
      // mid-write inside the next segment.
      usageAtFailureBytes: (await safeStorageEstimate())?.usage ?? null,
    });
    await writer.abort();
    throw error;
  }

  if (received !== segment.bytes) {
    throw new Error(
      `Segment ${segmentIndex} of ${filePath} received ${received} bytes; expected ${segment.bytes}`,
    );
  }

  const actualHash = bytesToHex(hash.digest());
  if (actualHash !== segment.sha256) {
    throw new Error(
      `Segment ${segmentIndex} of ${filePath} checksum mismatch (got ${actualHash}, expected ${segment.sha256})`,
    );
  }

  sessionBytesWritten += received;

  // Pressure telemetry: capture OPFS accounting AFTER write + close. The
  // delta between before/after tells us how much the UA thinks OPFS grew —
  // usually equal to segment bytes, but iOS has been suspected of counting
  // things oddly, so this lets us see the accounting directly.
  const estimateAfterClose = await safeStorageEstimate();
  // Read back the file we just wrote to confirm what the OS says is on disk
  // vs what the UA reports via storage.estimate(). Cheap: one fstat.
  const onDiskFileSize = await readOpfsFileSize(opfsPath);
  // Ground-truth OPFS walk only at file boundaries — expensive. Running at
  // the last segment of the file gives us a post-file snapshot directly
  // comparable to the pre-file walk captured at isFirstSegment.
  const walkAfterClose = options.isLastSegment ? await walkOpfsRoot() : null;
  // Double-poll: after a short delay, re-estimate to see if iOS reconciles
  // the reported `usage` downward. If it does, the inflation is a temporary
  // post-write bookkeeping effect rather than a persistent overcount.
  await safeDelay(SETTLE_POLL_DELAY_MS);
  const estimateAfterSettle = await safeStorageEstimate();
  const walkAfterSettle = options.isLastSegment ? await walkOpfsRoot() : null;

  const result: InstallFileSegmentResult = {
    segmentIndex,
    offset: segment.offset,
    bytes: segment.bytes,
    durationMs: Math.round(performance.now() - startedAt),
    mode: writer.mode,
    chunksSeen: chunkCount,
  };

  const usageBefore = estimateBefore?.usage ?? null;
  const usageClose = estimateAfterClose?.usage ?? null;
  const usageSettle = estimateAfterSettle?.usage ?? null;
  const settleDelta =
    usageClose !== null && usageSettle !== null ? usageSettle - usageClose : null;

  options.onTelemetry?.({
    event: "install.segment_done",
    data: {
      lane,
      manifest_id: manifestId,
      file: filePath,
      segment_index: segmentIndex,
      attempt_number: attemptNumber,
      offset: segment.offset,
      bytes: segment.bytes,
      mode: writer.mode,
      chunks_seen: chunkCount,
      elapsed_ms: result.durationMs,

      // Stream-level pressure (network + write path).
      ttfb_ms: ttfbMs,
      max_read_gap_ms: Math.round(maxReadGapMs),
      write_total_ms: Math.round(writeTotalMs),
      peak_chunk_bytes: peakChunkBytes,
      avg_chunk_bytes: chunkCount > 0 ? Math.round(received / chunkCount) : 0,

      // UA-reported origin storage (`navigator.storage.estimate()`). Captured
      // three times so we can separate "grows as we write" from "post-close
      // settle" from "reconciles later".
      opfs_usage_before_bytes: usageBefore,
      opfs_usage_after_close_bytes: usageClose,
      opfs_usage_after_settle_bytes: usageSettle,
      opfs_usage_delta_close_bytes:
        usageBefore !== null && usageClose !== null ? usageClose - usageBefore : null,
      opfs_usage_delta_settle_bytes: settleDelta,
      settle_delay_ms: SETTLE_POLL_DELAY_MS,
      opfs_quota_bytes:
        estimateAfterSettle?.quota ?? estimateAfterClose?.quota ?? estimateBefore?.quota ?? null,
      // `usageDetails` (Chromium) breaks `usage` down by storage bucket
      // (indexedDB, caches, fileSystem, ...). iOS does not provide it; null
      // there. When the bucket-level breakdown diverges from `usage`, that's
      // also diagnostic — non-OPFS storage growing during install would
      // explain pressure beyond what the segment writes account for.
      opfs_usage_details_after_close: (estimateAfterClose as StorageEstimate & { usageDetails?: Record<string, number> })?.usageDetails ?? null,
      opfs_usage_details_after_settle: (estimateAfterSettle as StorageEstimate & { usageDetails?: Record<string, number> })?.usageDetails ?? null,

      // Ground truth from the OPFS tree itself. If walk totals agree with
      // each other but disagree with `opfs_usage_*`, the UA's estimate is
      // lying. Walks only fire at first/last segment of each file to bound
      // the probe cost; null on intermediate segments.
      opfs_walk_before_total_bytes: walkBefore?.totalBytes ?? null,
      opfs_walk_after_close_total_bytes: walkAfterClose?.totalBytes ?? null,
      opfs_walk_after_settle_total_bytes: walkAfterSettle?.totalBytes ?? null,
      opfs_walk_before_file_count: walkBefore?.fileCount ?? null,
      opfs_walk_after_close_file_count: walkAfterClose?.fileCount ?? null,
      opfs_walk_after_settle_file_count: walkAfterSettle?.fileCount ?? null,
      opfs_walk_after_close_duration_ms: walkAfterClose?.durationMs ?? null,
      opfs_walk_after_settle_duration_ms: walkAfterSettle?.durationMs ?? null,
      opfs_walk_top_after_close: walkAfterClose?.top ?? null,
      is_first_segment: Boolean(options.isFirstSegment),
      is_last_segment: Boolean(options.isLastSegment),

      // Read-back of the file we just wrote. Comparing this to `bytes` tells
      // us whether the write really landed the expected bytes.
      file_size_after_close_bytes: onDiskFileSize,

      // Cumulative writes across this worker session. Compare to OPFS walk
      // totals: if walk total exceeds this plus the pre-session baseline,
      // something else is growing OPFS.
      session_bytes_written: sessionBytesWritten,
      session_bytes_before: sessionBytesBefore,
    },
  });

  return result;
}

/**
 * Remove the staged OPFS file for a manifest entry. Used by the orchestrator when
 * a segment checksum fails or when a fresh full re-install is required.
 */
export async function removeManifestFile(
  lane: string,
  manifestId: string,
  filePath: string,
): Promise<void> {
  const opfsPath = getOpfsPathForFilePath(lane, manifestId, filePath);
  await removeOpfsFile(opfsPath).catch(() => undefined);
}
